import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shift, listShifts, publishShifts, unpublishShifts, saveDayShifts } from '../services/shiftService';
import { RequestPeriod, listPeriods } from '../services/shiftRequestService';
import api from '../services/api';
import ShiftTimeline, { SERIES, Seg, TimelineRow } from './ShiftTimeline';

/**
 * シフトを組む。
 *
 * ## 予定と実績とは別の画面（代表判断 2026-08-29）
 *
 * 同じ画面に載せると **希望・予定・実績の3本**が並んでごちゃつく。
 * こちらは **希望（薄いグレー）と予定（水色の枠線）の2本**。単位も違って、
 * こちらは **受付期間ごと・未来**、予実は月ごと・過去。
 *
 * ## ⚠️ 希望は提出されたものだけを描く
 *
 * 定型（曜日ごと）で埋めない。埋めると**本人が言っていないことを希望として
 * 組む**ことになる。定型は昔のまま古いこともある。組む側に伝えるのは
 * 「未提出」という事実だけでよい（代表指摘 2026-08-29）。
 *
 * ## ⚠️ 公休は手で埋めない
 *
 * シフトが入らない日を1つずつ公休にして回るのは現実的でない。
 * **公開するときに、シフト制の人の未定をまとめて公休にする**（サーバ側）。
 */

const DAY = 24 * 60;
const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const mins = (t: string | null): number | null => {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** 終了が開始以下なら翌日にまたぐ。24:00 で切って、残りは翌日へ回す */
const toSegs = (startTime: string | null, endTime: string | null): { today: Seg; tomorrow?: Seg } | null => {
  const a = mins(startTime);
  const b0 = mins(endTime);
  if (a === null || b0 === null) return null;
  const b = b0 > a ? b0 : b0 + DAY;
  if (b <= DAY) return { today: { start: a, end: b } };
  return { today: { start: a, end: DAY, toNext: true }, tomorrow: { start: 0, end: Math.min(b - DAY, DAY), fromPrev: true } };
};

const daysBetween = (from: string, to: string): string[] => {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (d <= end) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    d.setDate(d.getDate() + 1);
  }
  return out;
};

interface Availability {
  userId: number;
  date: string;
  kind: 'available' | 'unavailable';
  startTime: string | null;
  endTime: string | null;
}
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const ShiftBuildPage: React.FC = () => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState<RequestPeriod[]>([]);
  const [periodId, setPeriodId] = useState<number | null>(null);
  const [period, setPeriod] = useState<RequestPeriod | null>(null);
  /**
   * 行に出す人。**対象者だけではない**（代表指摘 2026-09-02）。
   *
   * 退職した人や対象から外した人でも、**この期間にシフトを持っていれば行を出す**。
   * 出さないと、公開済みのシフト（公休を含む）が画面から消えて**直せなくなる**。
   */
  const [members, setMembers] = useState<
    {
      userId: number;
      name: string;
      submittedAt: string | null;
      isTarget?: boolean;
      retired?: boolean;
      reopened?: boolean;
    }[]
  >([]);
  const [entries, setEntries] = useState<Availability[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [day, setDay] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planBlocked, setPlanBlocked] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ start: string; end: string; breakMinutes: number }[]>([]);
  const [saving, setSaving] = useState(false);
  /** 公開したまま編集を開けている日。1日ずつしか開けない（開けっぱなしを防ぐ） */
  const [unlockedDay, setUnlockedDay] = useState<string | null>(null);

  useEffect(() => {
    listPeriods()
      .then((ps) => {
        setPeriods(ps);
        const open = ps.find((p) => p.status === 'open') ?? ps[0];
        setPeriodId(open?.id ?? null);
        if (!open) setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
        else setError(e.response?.data?.error || '受付期間の取得に失敗しました');
        setLoading(false);
      });
  }, []);

  const load = useCallback(async () => {
    if (!periodId) return;
    try {
      setLoading(true);
      setError('');
      const av = await api.get(`/shift-requests/${periodId}/availability`);
      const p: RequestPeriod = av.data.period;
      setPeriod(p);
      setMembers(av.data.members);
      setEntries(av.data.entries);

      const from = String(p.dateFrom).slice(0, 10);
      const to = String(p.dateTo).slice(0, 10);
      const sh = await listShifts(from, to);
      setShifts(sh.shifts);
      setDay((d) => (d && d >= from && d <= to ? d : from));
    } catch (e: any) {
      if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
      else setError(e.response?.data?.error || '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(
    () => (period ? daysBetween(String(period.dateFrom).slice(0, 10), String(period.dateTo).slice(0, 10)) : []),
    [period]
  );

  /*
   * その日その人の希望。**提出されたものだけ**。
   *
   * ⚠️ 定型（曜日ごと）で埋めない（代表指摘 2026-08-29）。埋めると
   * **本人が言っていないことを希望として組む**ことになる。定型は昔のまま
   * 古いこともある。組む側に伝えるのは「未提出」という事実だけでよい。
   */
  const availOf = (userId: number, date: string): Availability[] =>
    entries.filter((e) => e.userId === userId && String(e.date).slice(0, 10) === date);

  const shiftsOf = (userId: number, date: string) =>
    shifts.filter((s) => s.userId === userId && String(s.date).slice(0, 10) === date);

  const [view, setView] = useState<'day' | 'all'>('day');

  /** 前日から翌日へまたぐ帯も拾って、その日の行を組み立てる */
  const rowsFor = (day: string): TimelineRow[] => {
    const prev = days[days.indexOf(day) - 1];
    return members.map((m) => {
      const upper: Seg[] = [];
      const lower: Seg[] = [];

      for (const a of availOf(m.userId, day)) {
        if (a.kind !== 'available') continue;
        const seg = toSegs(a.startTime, a.endTime);
        if (seg) upper.push(seg.today);
      }
      /*
       * ⚠️ **まだ公開していない予定は破線で描く**（代表指摘 2026-09-02）。
       * 予実と同じ描き分けにして、画面をまたいで読み方を1つにする。
       */
      for (const s of shiftsOf(m.userId, day)) {
        if (s.isDayOff) continue;
        const seg = toSegs(s.startTime, s.endTime);
        if (seg) lower.push({ ...seg.today, draft: !s.publishedAt });
      }
      // 前日から続いている帯
      if (prev) {
        for (const a of availOf(m.userId, prev)) {
          const seg = a.kind === 'available' ? toSegs(a.startTime, a.endTime) : null;
          if (seg?.tomorrow) upper.push(seg.tomorrow);
        }
        for (const s of shiftsOf(m.userId, prev)) {
          const seg = s.isDayOff ? null : toSegs(s.startTime, s.endTime);
          if (seg?.tomorrow) lower.push({ ...seg.tomorrow, draft: !s.publishedAt });
        }
      }

      const av = availOf(m.userId, day);
      const isOff = av.some((a) => a.kind === 'unavailable');
      const dayOff = shiftsOf(m.userId, day).some((s) => s.isDayOff);
      /*
       * 何も無い行の理由を言い分ける。「出していない」と「出られない」は別。
       * 退職・対象外は**なぜこの人が並んでいるのか**の説明なので最優先で出す。
       */
      const note = m.retired
        ? '退職済み'
        : m.isTarget === false
          ? '対象外'
          : dayOff
            ? '公休'
            : isOff
              ? '出られない'
              : av.length === 0 && !m.submittedAt
                ? '未提出'
                : undefined;
      return {
        userId: m.userId,
        upper,
        lower,
        note,
        noteColor:
          m.retired || m.isTarget === false
            ? '#B45309'
            : dayOff
              ? '#64748B'
              : note === '未提出'
                ? '#94A3B8'
                : '#991B1B',
      };
    });
  };

  /**
   * 公開しているかどうか。**状態は受付期間が1つだけ持つ**（代表判断 2026-09-02）。
   *
   * 行ごとの `published_at` を集計していたときは「一部公開」という中間状態が
   * 生まれていた。**画面の操作では作れない状態**なのに、バッジもボタンも3通り必要だった。
   */
  const isPublished = Boolean(period?.shiftPublishedAt);

  /**
   * 公開済みの期間は**追加も含めて**編集できない（サーバも 409 で断る）。
   *
   * ただし**その日だけ開けられる**（代表判断 2026-09-02）。急な欠勤や交代のたびに
   * 期間ぜんたいを取り消すのは重すぎる（取り消すと自動公休が全員ぶん消えて作り直しになる）。
   *
   * ⚠️ **過ぎた日は開けない。** 実績を見てから予定を書き換えられると、遅刻・早退の
   * 証跡が消える。**過去を変えられるのは打刻修正だけ**。
   */
  const today = ymd(new Date());
  const canUnlock = (d: string) => isPublished && d >= today;
  const isLocked = () => isPublished && unlockedDay !== day;

  // 日を切り替えたら開放は閉じる。開けたまま別の日を触れないように
  useEffect(() => {
    setUnlockedDay(null);
  }, [day, period?.id]);

  const rows = useMemo(() => (day ? rowsFor(day) : []), [day, members, entries, shifts, days]);
  /** 期間まとめ。**帯のある行だけ**出す（全員×全日を並べると読めない） */
  const allDays = useMemo(
    () =>
      view === 'all'
        ? days.map((d) => [d, rowsFor(d).filter((r) => r.upper.length > 0 || r.lower.length > 0)] as [string, TimelineRow[]])
        : [],
    [view, days, members, entries, shifts]
  );

  /** 1時間ごとの人数（予定ベース）。必要人数と重ねる土台 */
  const coverage = useMemo(() => {
    const out: number[] = [];
    for (let h = 0; h < 24; h++) {
      out.push(rows.filter((r) => r.lower.some((s) => s.start < (h + 1) * 60 && s.end > h * 60)).length);
    }
    return out;
  }, [rows]);

  const openEditor = (userId: number) => {
    if (isLocked()) {
      setError(
        day && !canUnlock(day)
          ? '過ぎた日のシフトは直せません。実績の訂正は打刻修正で行ってください。'
          : 'この日は公開済みです。直すには「この日を編集」を押してください。'
      );
      return;
    }
    setError('');
    setEditing(userId);
    const cur = day ? shiftsOf(userId, day) : [];
    setDraft(
      cur
        .filter((s) => !s.isDayOff)
        .map((s) => ({ start: (s.startTime || '').slice(0, 5), end: (s.endTime || '').slice(0, 5), breakMinutes: s.breakMinutes }))
    );
  };

  /** 希望をそのまま予定にする。**組む手間のほとんどはこれで消える** */
  const fillFromRequest = (userId: number) => {
    if (!day) return;
    setDraft(
      availOf(userId, day)
        .filter((a) => a.kind === 'available')
        .map((a) => ({ start: (a.startTime || '').slice(0, 5), end: (a.endTime || '').slice(0, 5), breakMinutes: 0 }))
    );
  };

  const save = async (slots: any[]) => {
    if (!day || editing === null) return;
    try {
      setSaving(true);
      setError('');
      // 開けて直しているときだけ、公開を保ったまま入れ替える(サーバが履歴に残す)
      await saveDayShifts(editing, day, slots, isPublished && unlockedDay === day);
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const unpublish = async () => {
    if (!period) return;
    if (
      !window.confirm(
        `${period.name} の公開を取り消します。

・従業員の画面からこの期間のシフトが消えます
・公開のときに自動で入った公休は消え、「未定」に戻ります（手で置いた公休は残ります）
・希望の受付は自動では再開しません

よろしいですか？`
      )
    )
      return;
    try {
      setError('');
      const r = await unpublishShifts(period.id);
      await load();
      window.alert(`公開を取り消しました（${r.unpublished}件／自動の公休 ${r.removedDayOff}件を未定に戻しました）`);
    } catch (e: any) {
      setError(e.response?.data?.error || '公開の取り消しに失敗しました');
    }
  };

  /**
   * 公開する。
   *
   * ⚠️ **未提出の人が残っていることを先に見せる**（代表判断 2026-09-02）。
   * 締め切ってすぐ公開するので、未提出のまま公開する場面が普通に起きる。
   */
  const publish = async () => {
    if (!period) return;
    const yet = members.filter((m) => !m.submittedAt);
    const who = yet.length > 0 ? `

未提出のまま公開されます（${yet.length}人）: ${yet.map((m) => m.name).join('、')}` : '';
    if (
      !window.confirm(
        `${period.name} のシフトを従業員に公開します。

公開すると、この期間は編集できなくなります（直すには公開を取り消します）。
シフトの入っていない日は、シフト制の人に公休が入ります。${who}

よろしいですか？`
      )
    )
      return;
    try {
      setError('');
      const r = await publishShifts(period.id);
      await load();
      window.alert(`${r.published}件を公開しました（公休 ${r.filledDayOff}件を自動で入れました）`);
    } catch (e: any) {
      setError(e.response?.data?.error || '公開に失敗しました');
    }
  };

  if (planBlocked) {
    return (
      <div style={s.page}>
        <div style={s.planCard}>
          <h1 style={s.h1}>シフトを組む</h1>
          <p style={s.note}>
            シフト管理は<strong>シフト管理付きプラン</strong>でご利用いただけます。
          </p>
          <button onClick={() => navigate('/subscription')} style={s.primaryBtn}>プランを見る</button>
        </div>
      </div>
    );
  }

  const nameOf = (id: number) => members.find((m) => m.userId === id)?.name ?? `ID ${id}`;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={s.h1}>シフトを組む</h1>
            {period && (
              <span
                style={{
                  ...s.pill,
                  background: isPublished ? '#DCFCE7' : '#F1F5F9',
                  color: isPublished ? '#166534' : '#475569',
                }}
              >
                {isPublished ? '公開済み' : '未公開'}
              </span>
            )}
          </div>
          <p style={s.sub}>
            {isPublished
              ? '従業員に公開されています。直すには公開を取り消してください'
              : period && !period.submissionClosed
                ? '希望（薄いグレー）を見ながら、予定（水色の枠）を置きます。公開はシフト提出を締め切ってからです'
                : '希望（薄いグレー）を見ながら、予定（水色の枠）を置きます'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {periods.length > 0 && (
            <select value={periodId ?? ''} onChange={(e) => setPeriodId(Number(e.target.value))} style={s.input}>
              {periods.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <div style={{ display: 'inline-flex', border: '1px solid #CBD5E1', borderRadius: 6, overflow: 'hidden' }}>
            <button onClick={() => setView('day')} style={view === 'day' ? s.toggleOn : s.toggleOff}>1日ずつ</button>
            <button onClick={() => setView('all')} style={view === 'all' ? s.toggleOn : s.toggleOff}>期間まとめ</button>
          </div>
          {/*
            公開状況を出して、ボタンもそれに合わせる（代表指摘 2026-09-02）。
            「この期間を公開」が常に出ていると、公開済みかどうかが分からない。
          */}
          {period && !isPublished && (
            <button
              onClick={publish}
              disabled={!period.submissionClosed}
              title={period.submissionClosed ? undefined : 'シフト提出を締め切ってから公開できます'}
              style={period.submissionClosed ? s.primaryBtn : { ...s.primaryBtn, background: '#CBD5E1', cursor: 'not-allowed' }}
            >
              この期間を公開
            </button>
          )}
          {isPublished && (
            <button onClick={unpublish} style={{ ...s.backBtn, background: '#B45309' }}>公開を取り消す</button>
          )}
          <button onClick={() => navigate('/shift-comparison')} style={s.backBtn}>予定と実績</button>
          <button onClick={() => navigate('/shift-requests')} style={s.backBtn}>希望の受付</button>
          <button onClick={() => navigate('/dashboard')} style={s.backBtn}>戻る</button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {periods.length === 0 && !loading && (
        <div style={s.card}>
          <p style={s.note}>
            まだ受付がありません。<button onClick={() => navigate('/shift-requests')} style={s.linkBtn}>希望の受付</button>から作ってください。
          </p>
        </div>
      )}

      {period && (
        <>
          {/* 日を選ぶ。期間の中だけを出す */}
          <div style={{ display: view === 'day' ? 'flex' : 'none', gap: 4, overflowX: 'auto', paddingBottom: 6, marginBottom: 12 }}>
            {days.map((d) => {
              const w = new Date(`${d}T00:00:00`).getDay();
              const placed = shifts.some((x) => String(x.date).slice(0, 10) === d);
              return (
                <button
                  key={d}
                  onClick={() => setDay(d)}
                  style={{
                    flexShrink: 0,
                    border: day === d ? '2px solid #2563EB' : '1px solid #CBD5E1',
                    background: day === d ? '#EFF6FF' : '#fff',
                    borderRadius: 6,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    minWidth: 46,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{Number(d.slice(8))}</div>
                  <div style={{ fontSize: 10, color: w === 0 ? '#DC2626' : w === 6 ? '#2563EB' : '#64748B' }}>{WEEK[w]}</div>
                  {/* 組み終わった日が一目で分かるように */}
                  <div style={{ height: 3, marginTop: 3, borderRadius: 2, background: placed ? '#2563EB' : '#E2E8F0' }} />
                </button>
              );
            })}
          </div>

          {view === 'all' && !loading && (
            <div style={{ display: 'grid', gap: 14 }}>
              {allDays.map(([d, list]) => (
                <section key={d} style={s.card}>
                  <h2 style={{ margin: '0 0 10px', fontSize: 15 }}>
                    {d}（{WEEK[new Date(`${d}T00:00:00`).getDay()]}）
                    <span style={{ fontSize: 12, color: '#64748B', fontWeight: 400, marginLeft: 10 }}>{list.length}人</span>
                  </h2>
                  <ShiftTimeline
                    rows={list}
                    nameOf={nameOf}
                    upper={SERIES.wish}
                    lower={SERIES.plan}
                    onPick={(uid) => {
                      setDay(d);
                      openEditor(uid);
                    }}
                    emptyText="この日は希望も予定もありません。"
                  />
                </section>
              ))}
            </div>
          )}

          <section style={{ ...s.card, display: view === 'day' ? 'block' : 'none' }}>
            {loading ? (
              <p style={s.note}>読み込み中…</p>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '0 0 10px' }}>
                  <h2 style={{ margin: 0, fontSize: 15 }}>
                    {day}（{day ? WEEK[new Date(`${day}T00:00:00`).getDay()] : ''}）
                  </h2>

                  {/*
                    公開したまま、その日だけ開けて直す（代表判断 2026-09-02）。
                    急な欠勤や交代のたびに期間ぜんたいを取り消すのは重すぎるため。
                    **過ぎた日には出さない** — 実績を見てから予定を書き換えられると
                    遅刻・早退の証跡が消える。
                  */}
                  {isPublished && day && canUnlock(day) && unlockedDay !== day && (
                    <button onClick={() => setUnlockedDay(day)} style={s.backBtn}>この日を編集</button>
                  )}
                  {isPublished && day && unlockedDay === day && (
                    <>
                      <span style={{ ...s.pill, background: '#FEF3C7', color: '#92400E' }}>
                        編集中 — 保存すると従業員にすぐ反映されます
                      </span>
                      <button onClick={() => setUnlockedDay(null)} style={s.backBtn}>編集をやめる</button>
                    </>
                  )}
                  {isPublished && day && !canUnlock(day) && (
                    <span style={{ fontSize: 12, color: '#94A3B8' }}>
                      過ぎた日は直せません（実績の訂正は打刻修正で）
                    </span>
                  )}
                </div>
                <ShiftTimeline
                  rows={rows}
                  nameOf={nameOf}
                  upper={SERIES.wish}
                  lower={SERIES.plan}
                  onPick={openEditor}
                  emptyText="この受付の対象者がいません。"
                />

                {/* 時間ごとの人数。必要人数（Phase 4）と重ねる土台 */}
                <div style={{ display: 'flex', alignItems: 'flex-end', marginTop: 14, borderTop: '1px solid #E2E8F0', paddingTop: 10 }}>
                  <div style={{ width: 150, flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#475569' }}>時間ごとの人数</div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', height: 44, gap: 1 }}>
                    {coverage.map((n, i) => (
                      <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                        <div
                          title={`${String(i).padStart(2, '0')}:00 — ${n}人`}
                          style={{
                            height: `${(n / Math.max(1, ...coverage)) * 30}px`,
                            minHeight: 2,
                            background: n === 0 ? '#F1F5F9' : '#BFDBFE',
                            borderTop: n === 0 ? '2px solid #E2E8F0' : '2px solid #2563EB',
                          }}
                        />
                        <div style={{ fontSize: 9, color: '#94A3B8' }}>{n || ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>
        </>
      )}

      {editing !== null && day && (
        <div style={s.overlay} onClick={() => setEditing(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>{nameOf(editing)}</h2>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 12 }}>
              希望:{' '}
              {availOf(editing, day).length === 0
                ? '未提出'
                : availOf(editing, day).some((a) => a.kind === 'unavailable')
                  ? '出られない'
                  : availOf(editing, day)
                      .map((a) => `${(a.startTime || '').slice(0, 5)}–${(a.endTime || '').slice(0, 5)}`)
                      .join('、')}
            </div>

            <button onClick={() => fillFromRequest(editing)} style={{ ...s.backBtn, marginBottom: 12 }}>
              希望をそのまま入れる
            </button>

            <div style={{ display: 'grid', gap: 8 }}>
              {draft.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="time" value={r.start} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} style={s.input} />
                  <span>–</span>
                  <input type="time" value={r.end} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} style={s.input} />
                  <input
                    type="number"
                    min={0}
                    value={r.breakMinutes}
                    onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, breakMinutes: Number(e.target.value) } : x)))}
                    style={{ ...s.input, width: 70 }}
                    title="休憩(分)"
                  />
                  <button onClick={() => setDraft(draft.filter((_, j) => j !== i))} style={s.smallBtn}>削除</button>
                </div>
              ))}
              {/* 2枠目を足すと中抜けになる */}
              {draft.length < 6 && (
                <button onClick={() => setDraft([...draft, { start: '09:00', end: '18:00', breakMinutes: 60 }])} style={s.smallBtn}>
                  ＋ 枠を足す
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
              <button
                onClick={() =>
                  save(
                    draft
                      .filter((r) => r.start && r.end)
                      .map((r) => ({ isDayOff: false, startTime: r.start, endTime: r.end, breakMinutes: r.breakMinutes }))
                  )
                }
                disabled={saving}
                style={s.primaryBtn}
              >
                この時間で入れる
              </button>
              <button onClick={() => save([])} disabled={saving} style={s.linkBtn}>未定に戻す</button>
              <button onClick={() => setEditing(null)} style={s.linkBtn}>閉じる</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1200, margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  h1: { margin: 0, fontSize: 'clamp(18px, 4vw, 24px)' },
  sub: { margin: '4px 0 0', color: '#6c757d', fontSize: 13 },
  card: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 'clamp(12px, 3vw, 20px)' },
  planCard: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 32, textAlign: 'center', maxWidth: 520, margin: '40px auto' },
  note: { color: '#6c757d', fontSize: 13, lineHeight: 1.7, margin: 0 },
  error: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 4, padding: 10, marginBottom: 12 },
  input: { padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 },
  primaryBtn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 4, padding: '9px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  pill: { borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  backBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  smallBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  linkBtn: { background: 'none', border: 'none', color: '#0969da', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 4 },
  toggleOn: { background: '#2563EB', color: '#fff', border: 'none', padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  toggleOff: { background: '#fff', color: '#475569', border: 'none', padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 },
  modal: { background: '#fff', borderRadius: 12, padding: 20, width: 'min(94vw, 460px)' },
};

export default ShiftBuildPage;
